import type {
	ChangeEvent,
	ClipboardEvent,
	KeyboardEvent,
	InputHTMLAttributes
} from "react";

import { isValidEmail } from "src/utils/regex";

export interface TagInputProps extends InputHTMLAttributes<HTMLInputElement> {
	value: string;

	label?: string;

	tagsLimit?: number;

	helperText?: string;

	isLoading?: boolean;

	onInputChange?: (value: string) => void;

	onTagsChange?: (tags: string[]) => void;

	onTagLimitReached?: () => void;
}

const TagInput = ({
	value,
	label,
	tagsLimit = 10,
	isLoading = false,
	helperText,
	type = "email",
	onTagLimitReached,
	onTagsChange,
	onInputChange,
	...restProps
}: TagInputProps) => {
	const addTags = (tagsToAdd: string[]) => {
		if (tagsToAdd.length > tagsLimit) {
			return onTagLimitReached?.();
		}

		const validatedTags = tagsToAdd
			.map((e) => e.trim())
			.filter((tag) => isValidEmail(tag));

		onTagsChange?.(validatedTags);
	};

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		onInputChange?.(e.target.value);
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter" || e.key === "Tab") {
			e.preventDefault();

			const tagValues = value.split(",");
			addTags(tagValues);
		}
	};

	const handleBlur = () => {
		const tagValues = value.split(",");
		addTags(tagValues);
	};

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();

		const pastedData = e.clipboardData.getData("text");
		const pastedTags = pastedData.split(",");

		const combinedTags =
			value.length > 0 ? value.split(",").concat(pastedTags) : pastedTags;

		addTags(combinedTags);
		onInputChange?.(combinedTags.join(","));
	};

	const inputId = label
		? `tag-input-${label.replace(/\s+/g, "-").toLowerCase()}`
		: undefined;

	return (
		<div>
			<div>
				{label && (
					<label
						htmlFor={inputId}
						className="block text-zinc-600 font-medium mb-2"
					>
						{label}
					</label>
				)}

				{helperText && (
					<p className="text-zinc-600 text-sm mb-4">{helperText}</p>
				)}

				<div className="relative">
					<input
						id={inputId}
						type={type}
						value={value}
						onPaste={handlePaste}
						onKeyDown={handleKeyDown}
						onChange={handleChange}
						onBlur={handleBlur}
						className="w-full px-3 py-2 border border-border rounded-lg text-base focus:outline-none focus:ring-1 focus:ring-ring"
						{...restProps}
					/>

					{isLoading && (
						<div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
							<span>Loading...</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default TagInput;
